import { useShortcutActions } from 'app/_hooks'
import Image from 'next/image'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Colors } from '~/libs/mui/theme'
import { useLogger } from '../useLogger'
import { useVideoScale } from '../useVideoScale'
import { PlayerButton } from './PlayerButton'

type Props = {
  video: HTMLVideoElement
  flashIcon: (type: string) => void
}

export const Volume = ({ video, flashIcon }: Props) => {
  const { videoScale } = useVideoScale()
  const { mute: muteLog, unmute: unmuteLog, changeVolume: changeVolumeLog } = useLogger()
  const [volume, setVolume] = useState(video.volume)
  const barRef = useRef<HTMLDivElement>(null)
  const size = useMemo(() => videoScale * 52, [videoScale])

  const handleChangeVolume = useCallback(
    (clientX: number) => {
      if (!barRef.current) return

      const rect = barRef.current.getBoundingClientRect()
      const clickX = clientX - rect.left
      const ratio = Math.min(Math.max(clickX / rect.width, 0), 1)

      video.muted = false
      video.volume = ratio
      setVolume(ratio)
      changeVolumeLog()
    },
    [video, changeVolumeLog],
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleChangeVolume(e.clientX)

      const handleMouseMove = (e: MouseEvent) => {
        handleChangeVolume(e.clientX)
      }

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [handleChangeVolume],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      handleChangeVolume(e.clientX)
    },
    [handleChangeVolume],
  )

  const handleMute = useCallback(() => {
    video.muted = true
    setVolume(0)
    muteLog()
  }, [video, muteLog])

  const handleUnmute = useCallback(() => {
    video.muted = false

    const newVolume = video.volume === 0 ? 1 : video.volume
    video.volume = newVolume
    setVolume(newVolume)
    unmuteLog()
  }, [video, unmuteLog])

  const shortcutActions = useMemo(
    () => ({
      KeyM: () => {
        if (volume === 0) {
          handleUnmute()
          flashIcon('sound_on')
        } else {
          handleMute()
          flashIcon('sound_off')
        }
      },
      ArrowUp: () => {
        if (video.volume < 1) {
          video.volume = Math.min(video.volume + 0.1, 1)
          setVolume(video.volume)
          flashIcon('sound_on')
        }
      },
      ArrowDown: () => {
        if (video.volume > 0) {
          video.volume = Math.max(video.volume - 0.1, 0)
          setVolume(video.volume)
          if (video.volume === 0) {
            flashIcon('sound_off')
          } else {
            flashIcon('sound_on')
          }
        }
      },
    }),
    [video, volume, handleMute, handleUnmute, flashIcon],
  )
  useShortcutActions(shortcutActions)

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: `${videoScale * 24}px`,
      }}
    >
      {volume === 0 ? (
        <PlayerButton onClick={handleUnmute}>
          <Image src='/player/sound_off.png' alt='Mute' width={size} height={size} />
        </PlayerButton>
      ) : (
        <PlayerButton onClick={handleMute}>
          <Image src='/player/sound_on.png' alt='Volume' width={size} height={size} />
        </PlayerButton>
      )}
      <div
        ref={barRef}
        style={{
          width: `${videoScale * 120}px`,
          height: size,
          position: 'relative',
          cursor: 'pointer',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            width: '100%',
            height: `${videoScale * 4}px`,
            backgroundColor: Colors.color7E7E80,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            transformOrigin: 'left',
            transform: `translateY(-50%)`,
            width: `${volume * 100}%`,
            height: `${videoScale * 4}px`,
            backgroundColor: 'white',
          }}
        />
      </div>
    </div>
  )
}
